import {
  Box,
  Button,
  FieldText,
  IconButton,
  Text,
} from '@dixa/lib-shared-habla';
import type { Tag } from 'lib-shared-legacy-types';
import { ChangeEvent, useCallback, useState } from 'react';

import ColorField from 'components/shared/util/ColorField';
import { withTags, WithTagsInjectedProps } from 'hocs';
import { useNotifications } from 'hooks/useNotifications';
import { stringToHexColor } from 'utils/color/color';

import styles from './addTag.module.scss';

type OwnProps = {
  onAddTag: (tag: Tag) => void;
  onClose: () => void;
  tagName: string;
};

type Props = OwnProps & WithTagsInjectedProps;

function AddTagImpl({ onAddTag, onClose, tagName, tags }: Props): JSX.Element {
  const notify = useNotifications();
  const [isCreating, setIsCreating] = useState(false);
  const [{ color, name }, setState] = useState<{ color: string; name: string }>(
    {
      color: stringToHexColor(tagName),
      name: tagName,
    },
  );

  const handleNameChange = useCallback(
    (event?: ChangeEvent<HTMLInputElement>) => {
      if (event?.target.value) {
        setState((state) => ({
          ...state,
          name: event?.target.value,
        }));
      }
    },
    [],
  );

  const handleColorChange = useCallback((color: string) => {
    setState((state) => ({
      ...state,
      color,
    }));
  }, []);

  const handleAddTag = useCallback(() => {
    setIsCreating(true);
    tags
      .create({ color, name })
      .then(({ data: tag }) => {
        onAddTag(tag);

        notify({
          title: 'Tag created',
          text: `"${name}" tag has been created`,
          icon: 'i-done',
          className: 'success',
          time: 5000,
        });

        onClose();
      })
      .catch(() => {
        notify({
          title: 'An error occurred',
          text: `An error occurred when trying to create the "${name}" tag`,
          icon: 'i-exclamation',
          className: 'danger',
        });

        setIsCreating(false);
      });
  }, [color, name, notify, onAddTag, onClose, tags]);

  return (
    <Box className={styles.root}>
      <div className={styles.head}>
        <Text.Heading variant="heading-3">Add new tag</Text.Heading>
        <IconButton
          disabled={isCreating}
          variant="tertiary"
          icon="close"
          onClick={onClose}
        />
      </div>
      <FieldText
        label="Name"
        input={{
          name: 'tagName',
          onChange: handleNameChange,
          value: name,
        }}
        maxLength={255}
        placeholder="Name..."
        required
        type="text"
      />
      <ColorField
        label="Tag color"
        input={{
          name: 'tagColor',
          onChange: handleColorChange,
          value: color,
        }}
        showSpinner={false}
      />
      <Button
        className={styles.button}
        isLoading={isCreating}
        onClick={handleAddTag}
      >
        Create and add
      </Button>
    </Box>
  );
}

export const AddTag = withTags(AddTagImpl);
